import { Injectable } from '@angular/core';

import {
  AccountCreditPaymentResult,
  DiscountUsageLog,
  DiscountUsageLogFilters, Invoice,
  Payment,
  PaymentFilters,
  RefundLog,
  RefundLogFilters
} from '../../models/payment';
import { ListResponse } from '../../models/list-response';
import { ApiService } from '../api/api.service';
import { NgbModal } from '@ng-bootstrap/ng-bootstrap';
import { propExists } from '../../utils';
import { CustomerFilters } from '../../models/customer';
import { PaymentStatus } from '../../models/payment-enums';

// This Staff Site renders the Check again / Proceed anyway prompt; the server tells
// a tab that omits the field to refresh the page instead (PRO-1582).
export const LIVE_READER_PROMPT_SUPPORTED_FIELD = 'live_reader_prompt_supported';

export enum StripeTerminalPaymentStatus {
  succeeded = 'succeeded',
  failed = 'failed',
  pending = 'pending'
}

@Injectable({
  providedIn: 'root'
})
export class PaymentService {
  /**
   * A service providing access to payments via the API.
   */

  constructor(
    private apiService: ApiService
  ) {}

  /**
   * Get a page of payments from the API.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of payments per page.
   * @param filters - The set of filters to apply to the payments.
   * @param isOperational - Indicates to the API server that this is an
   *     "operational" query, which will use the primary database (as opposed
   *     to the replica).
   * @return The list of payments.
   */
  getPayments(pageNumber?: number,
              pageSize?: number,
              filters?: PaymentFilters,
              isOperational?: boolean): Promise<ListResponse<Payment>> {
    return this.apiService.getCollection<Payment, PaymentFilters>(
      'payments/',
      pageNumber,
      pageSize,
      filters,
      isOperational
    );
  }

  getPayment(id: number): Promise<Payment> {
    return this.apiService.getItem<Payment>(
      'payments/',
      id
    );
  }

  /**
   * Get a page of Refunds from the API.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of results per page.
   * @param filters - The set of filters to apply to the refunds.
   * @return The list of refunds.
   */
  getRefundLogs(pageNumber?: number,
                pageSize?: number,
                filters?: RefundLogFilters): Promise<ListResponse<RefundLog>> {
    return this.apiService.getCollection<RefundLog, RefundLogFilters>(
      'refunds/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }

  /**
   * Get a page of discount usage logs from the API.
   *
   * @param pageNumber - The requested page number.
   * @param pageSize - The requested number of results per page.
   * @param filters - The set of filters to apply to the logs.
   * @return The list of logs.
   */
  getDiscountUsageLogs(pageNumber?: number,
                       pageSize?: number,
                       filters?: DiscountUsageLogFilters): Promise<ListResponse<DiscountUsageLog>> {
    return this.apiService.getCollection<DiscountUsageLog, DiscountUsageLogFilters>(
      'discount-usages/',
      pageNumber,
      pageSize,
      filters,
      false
    );
  }

  /**
   * Initialize a Stripe payment via the API.
   *
   * @param invoiceId - The invoice which will be paid.
   * @param customerId - The customer with whom the payment will be associated.
   * @param paymentDescription - A brief description of the payment, will be
   *     displayed in Stripe.
   * @param successUrl - The URL to which the user will be navigated if the
   *     payment is successfully completed.
   * @param cancelUrl - The URL to which the user will be navigated if the
   *     payment is cancelled.
   * @param amount - The amount which will be paid. If this is absent, the
   *     payment will be for the full outstanding amount on the invoice.
   * @return The Stripe session URL where the user may complete the payment.
   */
  initStripePayment(invoiceId: number,
                    customerId: number,
                    paymentDescription: string,
                    successUrl: string,
                    cancelUrl: string,
                    amount?: number): Promise<string> {
    const body: any = {
      payment_description: paymentDescription,
      success_url: successUrl,
      cancel_url: cancelUrl
    };

    if (amount) {
      body.amount = amount;
    }

    return this.apiService.postToAPI<string>(
      'gym-endpoints/init-payment/stripe/' + invoiceId.toString() + '/' + customerId.toString() + '/',
      body
    );
  }

  initStripeTerminalPayment(invoiceId: number,
                            customerId: number,
                            paymentDescription: string,
                            readerId?: number,
                            amount?: number,
                            bypassLiveReaderCheck?: boolean): Promise<{payment_id: number, intent_client_secret: string}> {
    const body: any = {
      payment_description: paymentDescription
    };

    if (readerId) {
      body.reader_id = readerId;
    }
    if (amount) {
      body.amount = amount;
    }
    body[LIVE_READER_PROMPT_SUPPORTED_FIELD] = true;
    if (bypassLiveReaderCheck) {
      body.bypass_live_reader_check = true;
    }

    return this.apiService.postToAPI<{payment_id: number, intent_client_secret: string}>(
      'gym-endpoints/init-payment/stripe-terminal/' + invoiceId.toString() + '/' + customerId.toString() + '/',
      body
    );
  }

  checkStripeTerminalPayment(paymentId: number): Promise<{status: StripeTerminalPaymentStatus, payment: Payment}> {
    return this
      .getPayment(paymentId)
      .then(payment => {
        let status: StripeTerminalPaymentStatus;
        if (payment.status === PaymentStatus.paymentPending) {
          status = StripeTerminalPaymentStatus.pending;
        } else if (payment.status === PaymentStatus.paymentComplete) {
          status = StripeTerminalPaymentStatus.succeeded;
        } else {
          status = StripeTerminalPaymentStatus.failed;
        }
        return { status, payment };
      });
  }

  cancelStripeTerminalAction(readerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/cancel-stripe-terminal-action/' + readerId.toString() + '/',
      {}
    ).then();
  }

  cancelPayment(paymentId: number): Promise<PaymentStatus> {
    return this.apiService.postToAPI<{status: PaymentStatus}>(
      'gym-endpoints/cancel-payment/' + paymentId.toString() + '/',
      {}
    ).then(response => response.status);
  }

  /**
   * Initialize a Gravity payment via the API.
   *
   * @param invoiceId - The invoice which will be paid.
   * @param customerId - The customer with whom the payment will be associated.
   * @param amount - The amount which will be paid. If this is absent, the
   *     payment will be for the full outstanding amount on the invoice.
   * @return The Gravity transaction token used to carry out the payment.
   */
  initGravityPayment(invoiceId: number,
                     customerId: number,
                     amount?: number): Promise<string> {
    const body: any = {};

    if (amount) {
      body.amount = amount;
    }

    return this.apiService.postToAPI<string>(
      'gym-endpoints/init-payment/gravity/' + invoiceId.toString() + '/' + customerId.toString() + '/',
      body
    );
  }

  /**
   * Initialize an MX Merchant payment via the API.
   *
   * @param invoiceId - The invoice which will be paid.
   * @param customerId - The customer with whom the payment will be associated.
   * @param paymentDescription - A brief description of the payment, will be
   *     displayed in MX Merchant.
   * @param successUrl - The URL to which the user will be navigated if the
   *     payment is successfully completed.
   * @param cancelUrl - The URL to which the user will be navigated if the
   *     payment is cancelled.
   * @param amount - The amount which will be paid. If this is absent, the
   *     payment will be for the full outstanding amount on the invoice.
   * @param bypassLiveReaderCheck - Proceed although a cancelled card-reader
   *     payment on the invoice may still be live on the terminal (PRO-1582).
   * @return The MX Merchant payment url where the user may complete the payment.
   */
  initMXMerchantPayment(invoiceId: number,
                        customerId: number,
                        paymentDescription: string,
                        successUrl: string,
                        cancelUrl: string,
                        amount?: number,
                        bypassLiveReaderCheck?: boolean): Promise<string> {
    const body: any = {
      payment_description: paymentDescription,
      success_url: successUrl,
      cancel_url: cancelUrl
    };

    if (amount) {
      body.amount = amount;
    }
    body[LIVE_READER_PROMPT_SUPPORTED_FIELD] = true;
    if (bypassLiveReaderCheck) {
      body.bypass_live_reader_check = true;
    }

    return this.apiService.postToAPI<string>(
      'gym-endpoints/init-payment/mx-merchant/' + invoiceId.toString() + '/' + customerId.toString() + '/',
      body
    );
  }

  makeMockPayment(invoiceId: number,
                  customerId: number,
                  amount?: number): Promise<void> {
    const body: any = {};

    if (amount) {
      body.amount = amount;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/make-payment/mock/' + invoiceId.toString() + '/' + customerId.toString() + '/',
      body
    ).then();
  }

  makeOfflinePayment(invoiceId: number,
                     customerId: number,
                     amount?: number,
                     bypassLiveReaderCheck?: boolean): Promise<void> {
    const body: any = {};

    if (amount) {
      body.amount = amount;
    }
    body[LIVE_READER_PROMPT_SUPPORTED_FIELD] = true;
    if (bypassLiveReaderCheck) {
      body.bypass_live_reader_check = true;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/make-payment/offline/' + invoiceId.toString() + '/' + customerId.toString() + '/',
      body
    ).then();
  }

  changePaymentCustomer(paymentId: number, customerId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'gym-endpoints/change-payment-customer/' + paymentId.toString() + '/',
      {customer_id: customerId}
    ).then();
  }

  payInvoiceUsingSavedPaymentMethod(invoiceId: number,
                                    paymentMethodId: number,
                                    amount?: number,
                                    bypassLiveReaderCheck?: boolean): Promise<number> {
    const body: any = {};

    if (amount) {
      body.amount = amount;
    }
    body[LIVE_READER_PROMPT_SUPPORTED_FIELD] = true;
    if (bypassLiveReaderCheck) {
      body.bypass_live_reader_check = true;
    }

    return this.apiService.postToAPI<number>(
      'gym-endpoints/make-payment/saved-payment-method/' + invoiceId.toString() + '/' + paymentMethodId.toString() + '/',
      body
    );
  }

  payInvoiceUsingAccountCredit(invoiceId: number,
                               amount?: number,
                               bypassLiveReaderCheck?: boolean): Promise<AccountCreditPaymentResult> {
    const body: any = {};

    if (amount) {
      body.amount = amount;
    }
    body[LIVE_READER_PROMPT_SUPPORTED_FIELD] = true;
    if (bypassLiveReaderCheck) {
      body.bypass_live_reader_check = true;
    }

    return this.apiService.postToAPI<AccountCreditPaymentResult>(
      'gym-endpoints/make-payment/account-credit/' + invoiceId.toString() + '/',
      body
    );
  }

  markInvoiceWithNoAmountDueAsPaid(invoiceId: number): Promise<number> {
    return this.apiService.postToAPI<number>(
      'gym-endpoints/make-payment/no-amount-due/' + invoiceId.toString() + '/',
      {}
    );
  }

  getInvoice(invoiceId: number): Promise<Invoice> {
    return this.apiService.getFromAPI<Invoice>(
      'invoices/' + invoiceId.toString() + '/'
    );
  }

  /**
   * Refund an invoice via the API.
   *
   * @param invoiceId - The invoice which will be refunded.
   * @param customerId - The customer whose invoice will be refunded.
   * @param amount - The amount which will be refunded. If this is not provided, the invoice will be fully refunded.
   * @param adjustAmountDue - Whether to add the refunded amount to the invoice's amount due.
   */
  refundInvoice(invoiceId: number,
                customerId: number,
                amount?: number,
                adjustAmountDue?: boolean): Promise<void> {
    const body: any = {
      customer_id: customerId
    };
    if (propExists(amount)) {
      body.amount = amount;
    }
    if (propExists(adjustAmountDue)) {
      body.adjust_amount_due = adjustAmountDue;
    } else {
      body.adjust_amount_due = false;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/refund-invoice/' + invoiceId.toString() + '/',
      body
    ).then();
  }

  /**
   * Refund a payment via the API.
   *
   * @param paymentId - The payment which will be refunded.
   * @param amount - The amount which will be refunded. If this is not provided, the payment will be fully refunded.
   */
  refundPayment(paymentId: number,
                amount?: number): Promise<void> {
    const body: any = {};
    if (propExists(amount)) {
      body.amount = amount;
    }

    return this.apiService.postToAPI<any>(
      'gym-endpoints/refund-payment/' + paymentId.toString() + '/',
      body
    ).then();
  }

  downloadReceipt(paymentId: number,
                  invoiceId: number): Promise<void> {
    return this.apiService.downloadFromApi(
      'gym-endpoints/get-vat-receipt/' + paymentId.toString() + '/',
      'receipt-' + invoiceId.toString() + '.pdf'
    );
  }

  /**
   * Request a payment from a customer for an invoice. The customer is emailed
   * a tokenized link they can use to pay in the Climber App. Pass an empty
   * `recipientEmail` to create the request without sending any email - staff
   * then share the returned `payment_url` themselves.
   */
  requestPayment(invoiceId: number,
                 amount: number | null,
                 recipientEmail: string,
                 customNote: string): Promise<RequestedPaymentResponse> {
    return this.apiService.postToAPI<RequestedPaymentResponse>(
      'payment/requested-payment/create/',
      {
        invoice_id: invoiceId,
        amount: amount,
        recipient_email: recipientEmail,
        custom_note: customNote,
      }
    );
  }

  resendRequestedPaymentEmail(requestedPaymentId: number,
                              recipientEmail: string): Promise<void> {
    return this.apiService.postToAPI<any>(
      'payment/requested-payment/' + requestedPaymentId.toString() + '/resend-email/',
      {
        recipient_email: recipientEmail,
      }
    ).then();
  }

  cancelRequestedPayment(requestedPaymentId: number): Promise<void> {
    return this.apiService.postToAPI<any>(
      'payment/requested-payment/' + requestedPaymentId.toString() + '/cancel/',
      {}
    ).then();
  }

  previewRequestedPaymentEmail(invoiceId: number,
                               amount: number | null,
                               customNote: string): Promise<RequestedPaymentEmailPreview> {
    return this.apiService.postToAPI<RequestedPaymentEmailPreview>(
      'payment/requested-payment/preview-email/',
      {
        invoice_id: invoiceId,
        amount: amount,
        custom_note: customNote,
      }
    );
  }
}

export interface RequestedPaymentResponse {
  id: number;
  amount: string;
  is_full_amount: boolean;
  custom_note: string;
  created_at: string;
  // The login-free Climber App payment link staff can share themselves.
  payment_url: string;
  has_been_emailed: boolean;
}

export interface RequestedPaymentEmailPreview {
  subject: string;
  html: string;
}
